import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { articleService } from '../../api/articleService';
import { registrationService } from '../../api/registrationService';
import toast from 'react-hot-toast';
import { 
  FileText, 
  Tag,
  Calendar,
  User,
  Eye,
  Star,
  MessageSquare,
  Paperclip,
  Download,
  X,
  Shield,
  Settings,
  CheckCircle2
} from 'lucide-react';

const ViewArticleModal = ({ isOpen, onClose, article }) => {
  const [attachments, setAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [viewingFile, setViewingFile] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  useEffect(() => {
    if (isOpen && article) {
      loadAttachments();
      loadRoles();
    } else {
      setAttachments([]);
      setViewingFile(null);
      setRoles([]);
    }
  }, [isOpen, article]);

  const loadRoles = async () => {
    if (loadingRoles || roles.length > 0) return;
    
    setLoadingRoles(true);
    try {
      const response = await registrationService.getRoles();
      let rolesData = [];
      
      if (response?.data && Array.isArray(response.data)) {
        rolesData = response.data;
      } else if (Array.isArray(response)) {
        rolesData = response;
      }
      
      // Format roles for display: { roleId, roleName, roleCode }
      const formattedRoles = rolesData.map(role => ({
        roleId: role.roleId,
        roleName: role.label || role.roleName || 'Unknown Role',
        roleCode: role.roleCode || role.value || ''
      })).filter(role => role.roleId);
      
      setRoles(formattedRoles);
    } catch (error) {
      console.error('Error loading roles:', error);
      setRoles([]);
    } finally {
      setLoadingRoles(false);
    }
  };

  const loadAttachments = async () => {
    // Extract articleId from article object - handle different formats
    let articleId = article?.articleId;
    
    // If articleId is not available, try to extract from id field (e.g., "ART-008" -> 8)
    if (!articleId && article?.id) {
      const idStr = article.id.toString().replace('ART-', '').trim();
      articleId = parseInt(idStr);
      if (isNaN(articleId)) {
        console.warn('Could not extract articleId from article:', article);
        setAttachments([]);
        return;
      }
    }
    
    if (!articleId) {
      console.warn('No articleId available to load attachments');
      setAttachments([]);
      return;
    }
    
    setLoadingAttachments(true);
    try {
      //console.log('Loading attachments for articleId:', articleId);
      const response = await articleService.getArticleAttachments(articleId);
      //console.log('Attachments response:', response);
      
      // Handle different response structures
      // Backend ResponseModel returns: { status: "success", response: [...], httpStatus: 200 }
      // apiRequest may wrap it, so check multiple levels
      let attachmentsData = [];
      
      // Priority order for parsing:
      // 1. Direct response.response (from ResponseModel)
      if (response?.response && Array.isArray(response.response)) {
        attachmentsData = response.response;
      } 
      // 2. Wrapped in data.response
      else if (response?.data?.response && Array.isArray(response.data.response)) {
        attachmentsData = response.data.response;
      }
      // 3. Standard ResponseBean structure: { data: { data: [...] } }
      else if (response?.data?.data && Array.isArray(response.data.data)) {
        attachmentsData = response.data.data;
      }
      // 4. Double wrapped: { data: { data: { data: [...] } } }
      else if (response?.data?.data?.data && Array.isArray(response.data.data.data)) {
        attachmentsData = response.data.data.data;
      }
      // 5. Direct data array
      else if (response?.data && Array.isArray(response.data)) {
        attachmentsData = response.data;
      }
      // 6. Direct array
      else if (Array.isArray(response)) {
        attachmentsData = response;
      }
      
      //console.log('Parsed attachments:', attachmentsData);
      setAttachments(attachmentsData || []);
    } catch (error) {
      console.error('Error loading attachments:', error);
      // Don't show error toast - attachments are optional
      setAttachments([]);
    } finally {
      setLoadingAttachments(false);
    }
  };

  const getArticleId = () => {
    const articleId = article?.articleId || article?.id?.replace('ART-', '');
    return typeof articleId === 'string' ? parseInt(articleId) : articleId;
  };

  const getFileFormatId = (attachment) => {
    // Backend uses fileFormateId (with 'e'), but also check fileFormatId for compatibility
    return attachment?.fileFormateId || attachment?.fileFormatId || null;
  };

  const getFileTypeFromExtension = (fileName) => {
    if (!fileName) return null;
    const ext = fileName.split('.').pop()?.toLowerCase();
    // Map extensions to format IDs: 1=PNG, 2=PDF, 3=DOC/DOCX, 4=XLSX/JPG, 5=BMP, 6=MP4
    if (ext === 'pdf') return 2;
    if (ext === 'doc' || ext === 'docx') return 3;
    if (ext === 'xlsx' || ext === 'xlsm') return 4;
    if (ext === 'png') return 1;
    if (ext === 'bmp') return 5;
    if (ext === 'mp4') return 6;
    if (ext === 'jpg' || ext === 'jpeg') return 4;
    return null;
  };

  const canPreview = (attachment) => {
    // Get file format ID from attachment or infer from file extension
    const fileFormatId = getFileFormatId(attachment) || getFileTypeFromExtension(attachment?.fileName || attachment?.name);
    if (!fileFormatId) return false;
    
    // Can preview: PDF (2), Images (1, 4, 5)
    // Note: fileFormatId 4 can be XLSX or JPG, so we need to check the extension
    if (fileFormatId === 4) {
      const fileName = attachment?.fileName || attachment?.name || '';
      const ext = fileName.split('.').pop()?.toLowerCase();
      // If it's JPG/JPEG, we can preview; if it's XLSX, we cannot
      return ext === 'jpg' || ext === 'jpeg';
    }
    
    // For other types: 1=PNG (previewable), 2=PDF (previewable), 5=BMP (previewable)
    return [1, 2, 5].includes(fileFormatId);
  };

  const handleDownload = async (attachment) => {
    try {
      const articleId = getArticleId();
      if (!articleId) {
        toast.error('Article ID not found');
        return;
      }
      const blob = await articleService.downloadArticleAttachment(articleId, attachment.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.fileName || attachment.name || 'attachment';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('File downloaded successfully');
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file');
    }
  };

  const handleView = async (attachment) => {
    try {
      const articleId = getArticleId();
      if (!articleId) {
        toast.error('Article ID not found');
        return;
      }
      const blob = await articleService.downloadArticleAttachment(articleId, attachment.id);
      const url = window.URL.createObjectURL(blob);
      
      // Get file format ID from attachment or infer from file extension
      const fileFormatId = getFileFormatId(attachment) || getFileTypeFromExtension(attachment.fileName || attachment.name);
      
      setViewingFile({ 
        url, 
        name: attachment.fileName || attachment.name, 
        type: fileFormatId,
        mimeType: blob.type // Store MIME type for better detection
      });
    } catch (error) {
      console.error('Error viewing file:', error);
      toast.error('Failed to load file for viewing');
    }
  };

  const getFileIcon = (fileFormatId) => {
    // 1=PNG, 2=PDF, 3=DOC/DOCX, 4=XLSX/JPG, 5=BMP, 6=MP4
    switch (fileFormatId) {
      case 2: return '📄'; // PDF
      case 3: return '📝'; // DOC
      case 4: return '📊'; // XLSX or JPG
      case 5: return '🖼️'; // BMP
      case 6: return '🎥'; // MP4
      default: return '📎'; // Default
    }
  };

  if (!article) return null;

  const getStatusBadge = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower === 'published' || statusLower === 'active') {
      return <Badge className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">Active</Badge>;
    } else if (statusLower === 'draft') {
      return <Badge className="bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300">Draft</Badge>;
    } else {
      return <Badge className="bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-300">{status || 'Unknown'}</Badge>;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="View Article"
      size="xl"
    >
      <div className="space-y-6">
        {/* Header Section */}
        <div className="border-b pb-4">
          <div className="flex items-start justify-between mb-2">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{article.title || 'Untitled Article'}</h2>
            {getStatusBadge(article.status)}
          </div>
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mt-2">
            <div className="flex items-center gap-1">
              <FileText className="h-4 w-4" />
              <span className="font-medium">ID:</span> {article.id || article.articleId}
            </div>
            {article.category && (
              <div className="flex items-center gap-1">
                <Tag className="h-4 w-4" />
                <span>{article.category}</span>
              </div>
            )}
            {article.author && (
              <div className="flex items-center gap-1">
                <User className="h-4 w-4" />
                <span>By {article.author}</span>
              </div>
            )}
            {/* Permitted Roles - Inline with metadata */}
            <div className="flex items-center gap-1">
              <span className="font-medium">Permitted:</span>
              {loadingRoles ? (
                <span className="text-gray-500 text-xs">Loading roles...</span>
              ) : article.roleId ? (
                (() => {
                  const role = roles.find(r => r.roleId === article.roleId);
                  if (!role) {
                    return (
                      <Badge className="bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 px-2 py-1 flex items-center gap-1.5">
                        <Shield className="h-3.5 w-3.5" />
                        <span className="text-xs">Role ID: {article.roleId}</span>
                      </Badge>
                    );
                  }
                  
                  // Determine badge color and icon based on role code or name
                  const roleCode = (role.roleCode || '').toLowerCase();
                  const roleName = role.roleName || 'Unknown Role';
                  
                  let badgeClass = 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300';
                  let icon = <Settings className="h-3.5 w-3.5" />;
                  
                  if (roleCode.includes('network') || roleCode.includes('operator')) {
                    badgeClass = 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300';
                    icon = <CheckCircle2 className="h-3.5 w-3.5" />;
                  } else if (roleCode.includes('client') || roleCode.includes('customer') || roleCode.includes('specialist')) {
                    badgeClass = 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300';
                    icon = <User className="h-3.5 w-3.5" />;
                  } else if (roleCode.includes('admin') || roleCode.includes('lead') || roleCode.includes('system')) {
                    badgeClass = 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300';
                    icon = <Settings className="h-3.5 w-3.5" />;
                  } else {
                    badgeClass = 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300';
                    icon = <Shield className="h-3.5 w-3.5" />;
                  }
                  
                  return (
                    <Badge className={`${badgeClass} px-2 py-1 flex items-center gap-1.5`}>
                      {icon}
                      <span className="text-xs">{roleName}</span>
                    </Badge>
                  );
                })()
              ) : (
                <Badge className="bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 px-2 py-1 flex items-center gap-1.5">
                  <Shield className="h-3.5 w-3.5" />
                  <span className="text-xs">All Roles</span>
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Question Section */}
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-blue-600" />
            Question
          </h3>
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
              {article.question || 'No question provided'}
            </p>
          </div>
        </div>

        {/* Answer Section */}
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <FileText className="h-5 w-5 text-green-600" />
            Answer
          </h3>
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
              {article.answer || article.content || 'No answer provided'}
            </p>
          </div>
        </div>

        {/* Tags Section */}
        {article.tags && article.tags.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Tag className="h-5 w-5 text-purple-600" />
              Tags
            </h3>
            <div className="flex flex-wrap gap-2">
              {article.tags.map((tag, index) => (
                <Badge key={index} variant="secondary" className="px-3 py-1">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Attachments Section - Always show */}
        <div className="space-y-2 pt-4 border-t border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Paperclip className="h-5 w-5 text-blue-600" />
            Attachments {attachments.length > 0 && `(${attachments.length})`}
          </h3>
          {loadingAttachments ? (
            <div className="text-center py-4 text-gray-500">Loading attachments...</div>
          ) : attachments.length > 0 ? (
            <div className="space-y-2">
              {attachments.map((attachment, index) => (
                <div key={attachment.id || index} className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className="text-2xl flex-shrink-0">{getFileIcon(attachment.fileFormatId || attachment.fileFormateId)}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate" title={attachment.fileName || attachment.name}>
                        {attachment.fileName || attachment.name || `Attachment ${index + 1}`}
                      </p>
                      {attachment.createdOn && (
                        <p className="text-xs text-gray-500">
                          {new Date(attachment.createdOn).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {canPreview(attachment) && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleView(attachment)}
                        className="h-8 w-8 p-0 hover:bg-blue-50 hover:border-blue-300"
                        title="View"
                      >
                        <Eye className="h-4 w-4 text-blue-600" />
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(attachment)}
                      className="h-8 w-8 p-0 hover:bg-green-50 hover:border-green-300"
                      title="Download"
                    >
                      <Download className="h-4 w-4 text-green-600" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500 text-sm">No attachments available</div>
          )}
        </div>

        {/* Statistics Section */}
        <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 text-gray-600 dark:text-gray-400 mb-1">
              <Eye className="h-4 w-4" />
              <span className="text-sm">Views</span>
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{article.views || 0}</div>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 text-gray-600 dark:text-gray-400 mb-1">
              <Star className="h-4 w-4" />
              <span className="text-sm">Helpful</span>
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{article.helpful || 0}</div>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 text-gray-600 dark:text-gray-400 mb-1">
              <MessageSquare className="h-4 w-4" />
              <span className="text-sm">Not Helpful</span>
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{article.notHelpful || 0}</div>
          </div>
        </div>

        {/* Metadata Section */}
        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 dark:text-gray-400">
            {article.createdDate && (
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span><span className="font-medium">Created:</span> {article.createdDate}</span>
              </div>
            )}
            {article.lastModified && (
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span><span className="font-medium">Modified:</span> {article.lastModified}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* File Preview Modal */}
      {viewingFile && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4" onClick={() => setViewingFile(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl max-h-[90vh] w-full relative" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{viewingFile.name}</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setViewingFile(null)}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="p-4 overflow-auto max-h-[calc(90vh-80px)]">
              {(() => {
                const fileType = viewingFile.type;
                const fileName = viewingFile.name || '';
                const mimeType = viewingFile.mimeType || '';
                
                // PDF preview (type 2)
                if (fileType === 2 || mimeType.includes('pdf') || fileName.toLowerCase().endsWith('.pdf')) {
                  return <iframe src={viewingFile.url} className="w-full h-[600px] border-0" title={viewingFile.name} />;
                }
                
                // Image preview (types 1, 4, 5) or if MIME type indicates image
                if ([1, 4, 5].includes(fileType) || 
                    mimeType.startsWith('image/') || 
                    /\.(png|jpg|jpeg|gif|bmp|webp)$/i.test(fileName)) {
                  return <img src={viewingFile.url} alt={viewingFile.name} className="max-w-full h-auto mx-auto" />;
                }
                
                // Fallback: not previewable
                return (
                  <div className="text-center py-8 text-gray-500">
                    Preview not available for this file type. Please download to view.
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ViewArticleModal;

